import { Profile } from 'oidc-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserRole, SelectedUserRole } from '../../helpers/CheckPermissionHelper';
import { ClubsClient, ClubVm, TeamVm } from '../../utils/api';

/*
 * Every Insights screen is about one club. Who may pick which club comes from the
 * Arena claims we already have:
 *
 *   Admin      -> any club
 *   Federation -> clubs of the owned federation
 *   Club       -> locked to the owned club
 *
 * The API still enforces access; this only decides what the selector offers.
 */

const CLUB_STORAGE_KEY = 'insights.selectedClubId';
const TEAM_STORAGE_KEY = 'insights.selectedTeamId';
const CLUB_QUERY_KEY = 'clubId';

export interface InsightsClubScope {
    loading: boolean;
    /** Admin and Federation users choose; Club users are locked to their own club. */
    canSwitchClubs: boolean;
    clubs: ClubVm[];
    selectedClubId?: number;
    selectedClub?: ClubVm;
    /** Teams of the selected club (not every team in the database). */
    teams: TeamVm[];
    selectedTeamId?: number;
    teamsLoading: boolean;
    error?: string;
    selectClub: (clubId?: number) => void;
    selectTeam: (teamId?: number) => void;
}

export const parseClaimId = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = parseInt(String(value), 10);
    return Number.isNaN(parsed) || parsed <= 0 ? undefined : parsed;
};

export const clubLabel = (club?: ClubVm): string => (club ? club.nameUniversal || club.name : '');

/** Club team name plus Arena category (and gender when the API sent it). */
export const teamLabel = (team: TeamVm): string => {
    const name = team.nameUniversal || team.name || '';
    const category = (team.category || '').trim();
    const gender = (team.gender || '').replace(/_/g, ' ').trim();
    const extra = [category, gender].filter((part) => {
        if (!part) return false;
        return !name.toLowerCase().includes(part.toLowerCase());
    });
    if (extra.length > 1 && extra[0].toLowerCase().includes(extra[1].toLowerCase())) {
        extra.pop();
    }
    return extra.length ? `${name} (${extra.join(' · ')})` : name;
};

const readStoredId = (key: string): number | undefined => {
    try {
        return parseClaimId(window.sessionStorage.getItem(key) || undefined);
    } catch {
        return undefined;
    }
};

const writeStoredId = (key: string, id?: number): void => {
    try {
        if (id == null) window.sessionStorage.removeItem(key);
        else window.sessionStorage.setItem(key, id.toString());
    } catch {
        // Private mode / storage disabled: selection simply does not survive a reload.
    }
};

const readClubIdFromUrl = (): number | undefined =>
    parseClaimId(new URLSearchParams(window.location.search).get(CLUB_QUERY_KEY) || undefined);

/** Keeps deep links shareable without pulling react-router history through every page. */
const writeClubIdToUrl = (clubId?: number): void => {
    const url = new URL(window.location.href);
    if (clubId == null) url.searchParams.delete(CLUB_QUERY_KEY);
    else url.searchParams.set(CLUB_QUERY_KEY, clubId.toString());
    window.history.replaceState(window.history.state, '', url.toString());
};

const sortClubs = (clubs: ClubVm[]): ClubVm[] =>
    clubs.slice().sort((a, b) => clubLabel(a).localeCompare(clubLabel(b)));

// ponytail: module-level memo so moving between Insights pages does not refetch the
// club list on every mount. Ceiling: stale until reload; a club created meanwhile is
// missing from the picker. Upgrade path is a shared provider with invalidation.
let clubsCache: { key: string; clubs: ClubVm[] } | undefined;

const loadClubsForScope = async (
    userRole: SelectedUserRole,
    federationOwnerId?: number,
    clubOwnerId?: number
): Promise<ClubVm[]> => {
    const cacheKey = `${userRole.admin ? 'a' : ''}${userRole.federation ? 'f' : ''}${
        userRole.club ? 'c' : ''
    }:${federationOwnerId || 0}:${clubOwnerId || 0}`;

    if (clubsCache && clubsCache.key === cacheKey) return clubsCache.clubs;

    let clubs: ClubVm[];
    if (userRole.admin) {
        clubs = sortClubs((await new ClubsClient().getAll()) || []);
    } else if (userRole.federation && federationOwnerId) {
        const all = (await new ClubsClient().getAll()) || [];
        clubs = sortClubs(all.filter((club: ClubVm) => club.federationId === federationOwnerId));
    } else if (clubOwnerId) {
        clubs = [await new ClubsClient().getById(clubOwnerId)];
    } else {
        clubs = [];
    }

    clubsCache = { key: cacheKey, clubs };
    return clubs;
};

export const useInsightsClub = (userProfile?: Profile, enabled = true): InsightsClubScope => {
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : undefined),
        [userProfile]
    );
    const federationOwnerId = parseClaimId(userProfile && userProfile['federationOwner']);
    const clubOwnerId = parseClaimId(userProfile && userProfile['clubOwner']);
    const canSwitchClubs = !!userRole && (userRole.admin || userRole.federation);

    const [clubs, setClubs] = useState<ClubVm[]>([]);
    const [selectedClubId, setSelectedClubId] = useState<number | undefined>();
    const [teams, setTeams] = useState<TeamVm[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState<number | undefined>();
    const [loading, setLoading] = useState(true);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (!enabled || !userRole) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const available = await loadClubsForScope(userRole, federationOwnerId, clubOwnerId);
                if (cancelled) return;
                setClubs(available);

                const locked = !canSwitchClubs ? clubOwnerId : undefined;
                const candidates = [
                    locked,
                    readClubIdFromUrl(),
                    readStoredId(CLUB_STORAGE_KEY),
                    available.length ? available[0].id : undefined,
                ];
                const initial = candidates.find(
                    (id?: number) => id != null && available.some((club) => club.id === id)
                );

                setSelectedClubId(initial);
                writeStoredId(CLUB_STORAGE_KEY, initial);
                writeClubIdToUrl(initial);
            } catch {
                if (!cancelled) {
                    setClubs([]);
                    setSelectedClubId(undefined);
                    setError('Could not load clubs.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [enabled, userRole, federationOwnerId, clubOwnerId, canSwitchClubs]);

    useEffect(() => {
        let cancelled = false;

        const loadTeams = async (): Promise<void> => {
            if (!enabled || selectedClubId == null) {
                setTeams([]);
                setSelectedTeamId(undefined);
                return;
            }

            setTeamsLoading(true);
            try {
                const club = await new ClubsClient().getById(selectedClubId);
                if (cancelled) return;
                const clubTeams = (club.teams || [])
                    .slice()
                    .sort((a: TeamVm, b: TeamVm) => teamLabel(a).localeCompare(teamLabel(b)));
                setTeams(clubTeams);

                const stored = readStoredId(TEAM_STORAGE_KEY);
                const keep = clubTeams.some((team: TeamVm) => team.id === stored)
                    ? stored
                    : undefined;
                setSelectedTeamId(keep);
                writeStoredId(TEAM_STORAGE_KEY, keep);
            } catch {
                if (!cancelled) {
                    setTeams([]);
                    setSelectedTeamId(undefined);
                }
            } finally {
                if (!cancelled) setTeamsLoading(false);
            }
        };

        loadTeams();
        return () => {
            cancelled = true;
        };
    }, [enabled, selectedClubId]);

    const selectClub = useCallback(
        (clubId?: number): void => {
            if (!canSwitchClubs) return;
            setSelectedClubId(clubId);
            writeStoredId(CLUB_STORAGE_KEY, clubId);
            writeClubIdToUrl(clubId);
        },
        [canSwitchClubs]
    );

    const selectTeam = useCallback((teamId?: number): void => {
        setSelectedTeamId(teamId);
        writeStoredId(TEAM_STORAGE_KEY, teamId);
    }, []);

    const selectedClub = useMemo(
        () => clubs.find((club: ClubVm) => club.id === selectedClubId),
        [clubs, selectedClubId]
    );

    return {
        loading,
        canSwitchClubs,
        clubs,
        selectedClubId,
        selectedClub,
        teams,
        selectedTeamId,
        teamsLoading,
        error,
        selectClub,
        selectTeam,
    };
};
